import { useEffect, useMemo, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ArrowUpRight, BookAlert, BookOpen, ChevronDown, SearchX, X } from 'lucide-react';

import { Container } from '@/components/custom/container';
import { Input } from '@/components/ui/input';
import { useDashboardContext } from '@/stores/context/dashboard-layout-context';
import { useStore } from '@/stores';
import { useMediaQuery } from '@/lib/hooks/useMediaQuery';
import { useIsSubBusiness } from '@/lib/hooks/use-is-sub-business';
import { env } from '@/config/env';

import { IconMenu } from './icon-list';

const SidebarItems = () => {
  const { toggleSidebar } = useDashboardContext();
  const merchantAccess = useStore((state) => state.merchantAccess);
  const isSubBusiness = useIsSubBusiness();
  const currentPathname = useRouterState({ select: (s) => s.location.pathname });
  const isMobile = useMediaQuery('(max-width: 639px)');
  const [searchTerm, setSearchTerm] = useState('');

  const initialOpenGroups = useMemo(() => {
    const access = merchantAccess?.access || [];
    const result: Record<string, boolean> = {};

    access.forEach((item, idx) => {
      if (!item.group) return;
      const groupKey = item.group || `group-${idx}`;
      const isHome = item.group.toLowerCase() === 'home';
      const hasActiveRoute = item.role_access.some(
        (role) => role.route && currentPathname.startsWith(role.route),
      );
      if (isHome || hasActiveRoute) {
        result[groupKey] = true;
      }
    });

    return result;
  }, [merchantAccess?.access, currentPathname]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpenGroups);

  useEffect(() => {
    if (!isMobile) return;
    setOpenGroups((prev) => ({ ...initialOpenGroups, ...prev }));
  }, [initialOpenGroups, isMobile]);

  const toggleGroup = (groupKey: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const toTestId = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const filteredAccess = useMemo(() => {
    const access = merchantAccess?.access || [];

    const baseAccess = isSubBusiness
      ? access.map((group) => ({
          ...group,
          role_access: group.role_access.filter(
            (role) => role.route !== '/account-group' && role.route !== '/group-commission',
          ),
        }))
      : access;

    if (!normalizedSearch) return baseAccess;

    return baseAccess
      .map((group) => {
        if (!group.group) return null;
        const groupMatch = group.group.toLowerCase().includes(normalizedSearch);
        const roleAccess = groupMatch
          ? group.role_access
          : group.role_access.filter((role) => role.name?.toLowerCase().includes(normalizedSearch));

        if (roleAccess.length === 0) return null;

        return {
          ...group,
          role_access: roleAccess,
        };
      })
      .filter((group): group is NonNullable<typeof group> => Boolean(group));
  }, [merchantAccess?.access, normalizedSearch, isSubBusiness]);

  return (
    <Container
      direction="column"
      className="overflow-y-auto px-4 pb-2 sm:px-0 sm:pb-0"
      data-testid="sidebar-items"
    >
      <div className="sticky top-0 z-10 bg-white px-1.5 pt-1 pb-2">
        <div className="relative">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search menu"
            className="h-9 pr-8 text-sm"
            data-testid="sidebar-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      {normalizedSearch &&
        filteredAccess.length === 0 &&
        !'guidelines'.includes(normalizedSearch) && (
          <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
              <SearchX className="h-7 w-7 text-gray-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">No results found</p>
              <p className="mt-0.5 text-xs text-gray-400">
                No menu matches &quot;<span className="font-medium">{searchTerm}</span>&quot;
              </p>
            </div>
          </div>
        )}
      {filteredAccess.map((item, idx) => {
        if (!item.group) return null;
        const groupKey = item.group || `group-${idx}`;
        const isOpen = normalizedSearch ? true : (openGroups[groupKey] ?? false);
        const IconComponent =
          IconMenu.find((iconItems) => iconItems.name === item.group)?.icon || BookAlert;

        return (
          <div
            key={idx}
            className="text-gray-500 sm:rounded-none sm:border-0"
            data-testid={`sidebar-group-${toTestId(item.group)}`}
          >
            <button
              type="button"
              onClick={() => toggleGroup(groupKey)}
              className="flex min-h-10 w-full items-center justify-between rounded-md px-1.5 py-2 text-left text-sm leading-6 font-semibold text-gray-600 sm:rounded-none sm:py-3"
              data-testid={`sidebar-group-toggle-${toTestId(item.group)}`}
            >
              <div className="flex flex-row items-center gap-x-2">
                <IconComponent size={20} className="shrink-0" />
                <span className="pr-2 break-words">{item.group.toLocaleUpperCase()}</span>
              </div>
              <ChevronDown
                size={16}
                className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen &&
              item.role_access.map((sidebar, sidebarIdx) => {
                if (sidebar.name === 'Mode') return null;
                return (
                  <Link
                    to={sidebar.route}
                    key={sidebarIdx}
                    activeProps={{
                      className: 'text-white bg-brand-blue hover:bg-brand-blue hover:text-white',
                    }}
                    onClick={toggleSidebar}
                    className="hover:text-brand-blue flex min-h-10 items-center gap-3 rounded-md py-2 pr-2.5 pl-8 text-sm leading-5 font-normal break-words hover:bg-blue-50 sm:gap-4 sm:whitespace-nowrap"
                    data-testid={`sidebar-item-${toTestId(sidebar.name || `item-${sidebarIdx}`)}`}
                  >
                    <div className="leading-snug">{sidebar.name}</div>
                  </Link>
                );
              })}
          </div>
        );
      })}
      {(!normalizedSearch || 'guidelines'.includes(normalizedSearch)) && (
        <a
          href={env.urlGuidelineMerchant}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex min-h-10 w-full items-center justify-between rounded-md px-1.5 py-2 text-sm font-semibold text-gray-600 transition-opacity hover:opacity-70"
        >
          <div className="flex items-center gap-x-2">
            <BookOpen size={20} className="shrink-0" />
            GUIDELINES
          </div>
          <ArrowUpRight size={16} />
        </a>
      )}
    </Container>
  );
};

export default SidebarItems;
